import React from 'react';
import Modal from '../Modal';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Packaging {
  id: number;
  nom: string;
  type: string;
  capacite: number;
  unite: string;
  dimensions: string;
  poids_tare: number;
  statut: string;
  created_at: string;
  updated_at: string;
}

interface DeletePackagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  packaging: Packaging;
}

const DeletePackagingModal: React.FC<DeletePackagingModalProps> = ({ isOpen, onClose, onConfirm, packaging }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Supprimer le conditionnement" size="md">
      <div className="space-y-6">
        {/* Icône d'avertissement */}
        <div className="flex justify-center">
          <div className="p-3 bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Message de confirmation */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Êtes-vous sûr de vouloir supprimer ce conditionnement ?
          </h3>
          <p className="text-sm text-gray-600">
            Cette action est irréversible et supprimera définitivement le conditionnement de votre système.
          </p>
        </div>

        {/* Résumé du conditionnement à supprimer */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Conditionnement à supprimer :</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Nom :</span>
              <span className="font-medium text-gray-900">{packaging.nom}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type :</span>
              <span className="font-medium text-gray-900">{packaging.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Capacité :</span>
              <span className="font-medium text-gray-900">
                {packaging.capacite} {packaging.unite}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Dimensions :</span>
              <span className="font-medium text-gray-900">
                {packaging.dimensions || 'Non définies'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Poids de tare :</span>
              <span className="font-medium text-gray-900">
                {packaging.poids_tare ? `${packaging.poids_tare} kg` : 'Non défini'}
              </span>
            </div>
          </div>
        </div>

        {/* Avertissement */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Attention :</p>
              <p>La suppression de ce conditionnement peut affecter les produits et pesées associés.</p>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center"
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            Supprimer définitivement
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeletePackagingModal;
